import { Component, computed, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { format } from 'date-fns';
import html2PDF from 'jspdf-html2canvas';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { ContactHoursComponent } from '../../shared/components/contact-hours/contact-hours.component';
import { TelephoneLinkComponent } from '../../shared/components/telephone-link/telephone-link.component';
import { TimeFormatPipe } from '../../shared/pipes/time-format.pipe';
import { capitaliseText } from '../../shared/utils/formatting';
import { ClaimFormDataResponse } from '../book-claim/types/claim-form.types';
import { MmrService } from '../mmr/services/mmr.service';
import { VulnerableCustomersComponent } from './vulnerable-customers/vulnerable-customers.component';
import { AppInfo } from '../../global/environment';

@Component({
  selector: 'app-confirmation',
  imports: [ButtonComponent, TelephoneLinkComponent, ContactHoursComponent, VulnerableCustomersComponent],
  templateUrl: './confirmation.component.html',
  styleUrl: './confirmation.component.scss',
  host: {
    class: 'full-width mb-20',
  },
  providers: [TimeFormatPipe],
})
export class ConfirmationComponent implements OnInit {
  confirmedBooking = signal<ClaimFormDataResponse | undefined>(undefined);
  bookingSummaryItems: { label: string; value: string | undefined; testId: string }[] | undefined;

  planNumber = signal<string>('');
  private viewModel = computed(() => {
    const booking = this.confirmedBooking()!;

    const timeSlot = booking.state?.currentVisit?.timeSlot;
    const serviceProvider = timeSlot.serviceProvider;
    const reference = booking.state?.claimReference;
    const fault = booking.state?.faultCode?.title;
    const address = booking.state?.item?.address;
    const contact = booking.state?.contact;
    const modelNumber = booking.state?.item?.modelNumber;
    const formattedRepairerName = capitaliseText(serviceProvider?.companyName);
    const formattedApplianceName = capitaliseText(booking.state?.item?.description);

    return {
      confirmedBookingLoaded: !!this.confirmedBooking(),
      mmrLink: ['/', this.planNumber(), 'claims'],
      fault,
      reference,
      contact,
      phoneNumber: (contact?.phonePrimary || '').replace('+44', '0'),
      serviceProvider,
      formattedRepairerName,
      formattedApplianceName,
      modelNumber,
      planNumber: this.planNumber(),
      currentAppointment: timeSlot,
      currentStep: this.confirmedBooking()?.state?.currentStep,
      appointment: {
        allDay: timeSlot!.allDay,
        date: timeSlot!.startDate,
        endTime: timeSlot!.endTime,
        startTime: timeSlot!.startTime,
      },
      address: [...(address?.lines || []), address?.postalcode].filter((i) => !!i).map((i) => (i ?? '').toLowerCase()),
    };
  });

  get vm() {
    return this.viewModel();
  }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly mmrService: MmrService,
    private readonly timeFormatPipe: TimeFormatPipe
  ) {}

  ngOnInit() {
    this.planNumber.set(this.route.snapshot.params['planNumber']);
    const loadedConfirmBooking = sessionStorage.getItem('confirmedBooking');

    // Ensure we only show the confirmation page to people who have just booked
    if (!loadedConfirmBooking) {
      window.location.href = `${AppInfo.Environment.myAccountURL}/my-plans`;
      return;
    }

    // this.mmrService.reloadRepair();
    // this.mmrService.reloadHistory();
    this.confirmedBooking.set(JSON.parse(loadedConfirmBooking!));

    this.bookingSummaryItems = [
      {
        label: 'Engineer visit',
        value: this.formattedAppointmentDate,
        testId: 'visit-date',
      },
      {
        label: 'Your appliance',
        value: this.vm.formattedApplianceName,
        testId: 'appliance-name',
      },
      {
        label: `What's wrong`,
        value: this.vm.fault,
        testId: 'fault-name',
      },
      {
        label: 'Model number',
        value: this.vm.modelNumber,
        testId: 'model-number',
      },
      {
        label: 'Plan number',
        value: this.vm.planNumber,
        testId: 'plan-number',
      },
      {
        label: 'Reference',
        value: this.formattedReferenceNumber,
        testId: 'reference-number',
      },
    ].filter((i) => i.value && i.value !== '');
  }

  clearConfirmedBooking(): void {
    sessionStorage.removeItem('confirmedBooking');
  }

  get formattedReferenceNumber(): string | undefined {
    return this.vm.reference?.replace(/^GBR\s*/, '');
  }

  get formattedAppointmentDate(): string | undefined {
    if (!this.vm.appointment.date) return;
    const apptDate = format(this.vm.appointment.date, 'do MMMM yyyy');
    const dayOfWeek = format(new Date(this.vm.appointment.date), 'EEEE');
    return `${dayOfWeek} ${apptDate} at ${this.timeFormatPipe.transform(this.vm.appointment.startTime)} - ${this.timeFormatPipe.transform(this.vm.appointment.endTime)}`;
  }

  handleDownload(): void {
    const page = document.querySelector<HTMLElement>('#page-root')?.cloneNode(true);

    const copyContentHolder = document.querySelector<HTMLElement>('#copy-content');
    if (!copyContentHolder) {
      throw new Error('Could not find copy content holder');
    }
    copyContentHolder.innerHTML = '';

    copyContentHolder.appendChild(page!);

    html2PDF(copyContentHolder, {
      jsPDF: {
        format: 'a4',
      },
      imageType: 'image/jpeg',
      output: 'booking-confirmed.pdf',
    }).then(() => {
      copyContentHolder.innerHTML = '';
    });
  }
}
